import React, { useCallback, useMemo, useState } from 'react';
import { Drawer, Table, Row } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import sortByString, { safeExtractFromObject } from '../../helpers/helpers';
import { confirmOvertimeProfileAssignment } from '../../helpers/overtime/overtimeSettingsHelpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
];

export default function OTProfileAssignDrawer({
  visible,
  onClose,
  type,
  assignedIds,
  onAssign,
}) {
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const unassignedData = useMemo(() => {
    const assignedSet = new Set(assignedIds || []);
    if (type === 'users') {
      return users.filter((u) => !assignedSet.has(u.id) && u.active).sort(sortByString('name'));
    }
    return Object.values(divisions)
      .filter((d) => d && !assignedSet.has(d.id))
      .map(({ id, name }) => ({ id, name }))
      .sort(sortByString('name'));
  }, [type, users, divisions, assignedIds]);

  const handleSubmit = useCallback(() => {
    confirmOvertimeProfileAssignment({
      type,
      onConfirm: () => {
        onAssign(selectedRowKeys);
        setSelectedRowKeys([]);
        onClose();
      },
    });
  }, [type, onAssign, selectedRowKeys, onClose]);

  const handleClose = useCallback(() => {
    setSelectedRowKeys([]);
    onClose();
  }, [onClose]);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return { className: 'ontraccr-table-header' };
  }, [settings]);

  const title = type === 'users'
    ? 'Assign users to this OT profile'
    : 'Assign divisions to this OT profile';

  return (
    <Drawer
      title={title}
      visible={visible}
      onClose={handleClose}
      width={420}
      bodyStyle={{ padding: 16 }}
      footer={(
        <Row justify="end" gutter={8}>
          <OnTraccrButton title="Cancel" type="cancel" onClick={handleClose} style={{ marginRight: 8 }} />
          <OnTraccrButton
            title="Assign"
            type="primary"
            disabled={selectedRowKeys.length === 0}
            onClick={handleSubmit}
          />
        </Row>
      )}
    >
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={unassignedData}
        rowKey="id"
        onHeaderRow={onHeaderRow}
        rowSelection={{
          fixed: true,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: () => ({
            className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
          }),
        }}
      />
    </Drawer>
  );
}

OTProfileAssignDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['users', 'divisions']).isRequired,
  profileId: PropTypes.string.isRequired,
  assignedIds: PropTypes.arrayOf(PropTypes.string),
  onAssign: PropTypes.func.isRequired,
};

OTProfileAssignDrawer.defaultProps = {
  assignedIds: [],
};
